import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Star, Music2 } from "lucide-react";
import { api, formatDuration } from "../lib/api";
import { adImpression, adClick } from "../lib/adstats";
import { usePlayer } from "../contexts/PlayerContext";

/** Artiste en vedette ⭐ + Single de la semaine 🎵 (offres MBOAZICK ADS). */
export default function SpotlightSection() {
    const [data, setData] = useState(null);
    const { current, playing, play, toggle } = usePlayer();

    useEffect(() => {
        api.get("/spotlight").then(({ data }) => setData(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!data) return;
        if (data.featured_artist) adImpression("artist", data.featured_artist.id);
        if (data.single_of_week) adImpression("single", data.single_of_week.id);
    }, [data]);

    if (!data || (!data.featured_artist && !data.single_of_week)) return null;
    const a = data.featured_artist;
    const s = data.single_of_week;
    const tracks = data.featured_tracks || [];
    const playable = tracks.filter((t) => t.preview_url || t.audio_url);

    const playTrack = (t, queue, adType, adId) => {
        adClick(adType, adId);
        if (current?.id === t.id) { toggle(); return; }
        play(t, { queue });
    };

    return (
        <section className="border-t border-[#1c1c1c] bg-[#0a0a0a]" data-testid="spotlight-section">
            <div className="max-w-7xl mx-auto px-4 md:px-12 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {a && (
                    <div className="relative overflow-hidden rounded-2xl border border-[#ffc83d]/30 bg-[#101010]" data-testid="featured-artist-card">
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent z-10" />
                        {(a.cover_url || a.avatar_url) && (
                            <img decoding="async" loading="lazy" src={a.cover_url || a.avatar_url} alt={a.stage_name || a.name} className="absolute inset-0 w-full h-full object-cover opacity-50" />
                        )}
                        <div className="relative z-20 p-6 md:p-8 flex flex-col justify-end min-h-[320px]">
                            <span className="inline-flex w-fit items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#0F0F0F] bg-[#ffc83d] px-3 py-1 rounded-full">
                                <Star className="w-3 h-3" /> Artiste en vedette
                            </span>
                            <div className="mt-4 flex items-center gap-4">
                                {a.avatar_url && <img decoding="async" loading="lazy" src={a.avatar_url} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-2 ring-[#ffc83d]" />}
                                <div>
                                    <h3 className="font-display text-2xl md:text-3xl font-semibold">{a.stage_name || a.name}</h3>
                                    <p className="text-sm text-[#C8C8C8]">{[a.genre, a.country].filter(Boolean).join(" • ")}</p>
                                </div>
                            </div>
                            {tracks.length > 0 && (
                                <div className="mt-4 space-y-1.5">
                                    {tracks.map((t) => (
                                        <div key={t.id} className="flex items-center gap-2.5 text-sm">
                                            {(t.preview_url || t.audio_url) ? (
                                                <button onClick={() => playTrack(t, playable, "artist", a.id)} className="w-7 h-7 rounded-full bg-[#00d97e] text-[#0F0F0F] flex items-center justify-center shrink-0" data-testid={`featured-track-play-${t.id}`}>
                                                    {current?.id === t.id && playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-px" />}
                                                </button>
                                            ) : <Music2 className="w-4 h-4 text-[#6E6E6E]" />}
                                            <span className="truncate text-[#F4F4F4]">{t.title}</span>
                                            <span className="text-xs text-[#6E6E6E] font-mono ml-auto">{formatDuration(t.duration)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-5 flex gap-3">
                                {playable[0] && (
                                    <button onClick={() => playTrack(playable[0], playable, "artist", a.id)} className="inline-flex items-center gap-2 bg-[#00d97e] hover:bg-[#00f099] text-[#0F0F0F] text-sm font-bold px-5 py-2.5 rounded-full transition-colors" data-testid="featured-artist-listen">
                                        <Play className="w-4 h-4" /> Écouter
                                    </button>
                                )}
                                {a.username && (
                                    <Link to={`/artistes/${a.username}`} onClick={() => adClick("artist", a.id)} className="inline-flex items-center gap-2 border border-[#ffc83d]/50 text-[#ffc83d] hover:bg-[#ffc83d]/10 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors" data-testid="featured-artist-profile">
                                        Voir le profil
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {s && (
                    <div className="relative overflow-hidden rounded-2xl border border-[#00c0ff]/30 bg-[#101010] flex flex-col md:flex-row" data-testid="single-of-week-card">
                        <div className="relative md:w-1/2 aspect-square shrink-0">
                            {s.cover_url ? (
                                <img decoding="async" loading="lazy" src={s.cover_url} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                            ) : (
                                <div className="absolute inset-0 bg-gradient-to-br from-[#00c0ff]/20 to-[#0F0F0F] flex items-center justify-center"><Music2 className="w-12 h-12 text-[#00c0ff]/40" /></div>
                            )}
                            {(s.preview_url || s.audio_url) && (
                                <button onClick={() => playTrack(s, [s], "single", s.id)} className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/15 transition-colors" data-testid="single-of-week-play">
                                    <span className="w-16 h-16 rounded-full bg-[#00d97e] text-[#0F0F0F] flex items-center justify-center shadow-2xl">
                                        {current?.id === s.id && playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                                    </span>
                                </button>
                            )}
                        </div>
                        <div className="p-6 md:p-8 flex flex-col justify-center">
                            <span className="inline-flex w-fit items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#0F0F0F] bg-[#00c0ff] px-3 py-1 rounded-full">
                                🎵 Single de la semaine
                            </span>
                            <h3 className="mt-4 font-display text-2xl md:text-3xl font-semibold">{s.title}</h3>
                            <p className="mt-1 text-sm text-[#C8C8C8]">{s.artist_name} {s.genre ? `• ${s.genre}` : ""}</p>
                            <p className="mt-1 text-xs text-[#6E6E6E]">{s.plays || 0} écoutes</p>
                            <Link to={`/track/${s.slug || s.id}`} onClick={() => adClick("single", s.id)} className="mt-5 inline-flex w-fit items-center gap-2 border border-[#00c0ff]/50 text-[#00c0ff] hover:bg-[#00c0ff]/10 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors" data-testid="single-of-week-link">
                                À écouter maintenant
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
